import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Mail } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

type EmailSettings = {
  id: string;
  is_enabled: boolean;
  provider: 'aws_ses' | 'brevo';
  aws_access_key_id: string | null;
  aws_secret_access_key: string | null;
  aws_region: string | null;
  aws_from_email: string | null;
  brevo_api_key: string | null;
  brevo_from_email: string | null;
};

export const EmailProviderSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('email_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (!data) {
        const { data: newSettings, error: createError } = await supabase
          .from('email_settings')
          .insert({
            is_enabled: false,
            provider: 'brevo',
          })
          .select()
          .single();

        if (createError) throw createError;
        setSettings(newSettings as EmailSettings);
      } else {
        setSettings(data as EmailSettings);
      }
    } catch (error: any) {
      toast({
        title: "Error loading email settings",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('email_settings')
        .update({
          is_enabled: settings.is_enabled,
          provider: settings.provider,
          aws_access_key_id: settings.aws_access_key_id,
          aws_secret_access_key: settings.aws_secret_access_key,
          aws_region: settings.aws_region,
          aws_from_email: settings.aws_from_email,
          brevo_api_key: settings.brevo_api_key,
          brevo_from_email: settings.brevo_from_email,
        })
        .eq('id', settings.id);

      if (error) throw error;

      toast({
        title: "Settings saved",
        description: "Email provider settings have been updated successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error saving settings",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">No email settings found.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader>
          <CollapsibleTrigger className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              <div className="text-left">
                <CardTitle>Email Provider Settings</CardTitle>
                <CardDescription>Configure email notifications provider</CardDescription>
              </div>
            </div>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="email-enabled">Enable Email Notifications</Label>
              <Switch
                id="email-enabled"
                checked={settings.is_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
              />
            </div>

            {settings.is_enabled && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="provider">Email Provider</Label>
                  <Select
                    value={settings.provider}
                    onValueChange={(value: 'aws_ses' | 'brevo') => setSettings({ ...settings, provider: value })}
                  >
                    <SelectTrigger id="provider">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="brevo">Brevo (Sendinblue)</SelectItem>
                      <SelectItem value="aws_ses">AWS SES</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {settings.provider === 'brevo' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="brevo-api-key">Brevo API Key</Label>
                      <Input
                        id="brevo-api-key"
                        type="password"
                        value={settings.brevo_api_key || ''}
                        onChange={(e) => setSettings({ ...settings, brevo_api_key: e.target.value })}
                        placeholder="Enter your Brevo API key"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="brevo-from-email">From Email Address</Label>
                      <Input
                        id="brevo-from-email"
                        type="email"
                        value={settings.brevo_from_email || ''}
                        onChange={(e) => setSettings({ ...settings, brevo_from_email: e.target.value })}
                        placeholder="notifications@yourdomain.com"
                      />
                    </div>
                  </>
                )}

                {settings.provider === 'aws_ses' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="aws-access-key">AWS Access Key ID</Label>
                      <Input
                        id="aws-access-key"
                        type="password"
                        value={settings.aws_access_key_id || ''}
                        onChange={(e) => setSettings({ ...settings, aws_access_key_id: e.target.value })}
                        placeholder="Enter AWS Access Key ID"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="aws-secret-key">AWS Secret Access Key</Label>
                      <Input
                        id="aws-secret-key"
                        type="password"
                        value={settings.aws_secret_access_key || ''}
                        onChange={(e) => setSettings({ ...settings, aws_secret_access_key: e.target.value })}
                        placeholder="Enter AWS Secret Access Key"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="aws-region">AWS Region</Label>
                      <Input
                        id="aws-region"
                        value={settings.aws_region || 'us-east-1'}
                        onChange={(e) => setSettings({ ...settings, aws_region: e.target.value })}
                        placeholder="us-east-1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="aws-from-email">From Email Address</Label>
                      <Input
                        id="aws-from-email"
                        type="email"
                        value={settings.aws_from_email || ''}
                        onChange={(e) => setSettings({ ...settings, aws_from_email: e.target.value })}
                        placeholder="notifications@yourdomain.com"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Settings
            </Button>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
